import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Discount rules.
 *
 * The old modal previewed a discount and then queued a BILLING_DISCOUNT action —
 * so the number on screen came down, the sync dispatcher logged an intent, and
 * the printed bill charged full price.
 *
 * There is nothing to POST here: a discount is not a separate mutation. It is an
 * argument to POST /orders/{id}/bill, which is fired at settle. So this form's
 * job is to set the discount the settle path will send, and to say plainly that
 * it applies when the bill is generated. The totals shown are the server's — the
 * preview endpoint recomputes them with the discount applied.
 *
 * The cap is enforced server-side; showing it here only saves a round trip.
 */
export default function DiscountForm({
  order,
  preview,
  settings,
  discount,
  onApply,
  onClear,
  onDone,
}) {
  // Percentage is the default because it is the first option in the select below;
  // deriving it from `discountPct != null` made the state say "flat" while the
  // control displayed "Percentage", so the first discount of the session went out
  // as a flat rupee amount whatever the cashier saw.
  const [rule, setRule] = useState(discount.discount > 0 ? "flat" : "pct");
  const [value, setValue] = useState(
    discount.discountPct != null ? discount.discountPct : discount.discount || 0
  );
  const [reason, setReason] = useState(discount.reason || "");
  const [error, setError] = useState(null);

  // The cap the SERVER will enforce: restaurant_settings.discount_max_pct, read by
  // billing_service._compute_bill, exposed to the terminal through
  // GET /api/settings/display and mapped in lib/settingsMap.js. This used to read a
  // key nothing ever wrote and fall back to 100, so the hint promised "Cap 100%" on an
  // outlet capped at 20 and the form green-lit a discount the bill would refuse.
  //
  // Null when the terminal has not been told the cap (settings/display not fetched
  // yet, or an under-migrated database without the row). In that case the form states
  // no cap and skips the local guard — the server's rejection is the honest single
  // source of the rule; inventing one here is what caused this bug.
  const configuredCap = Number(settings?.discountMaxPct);
  const cap = Number.isFinite(configuredCap) ? configuredCap : null;
  const subtotal = Number(preview?.subtotal || 0);
  const applied = Number(preview?.discount_amount || 0);
  const tierPct = Number(preview?.tier_discount_pct || 0);
  // The outlet's own rules (2026-11-01): whether a reason is required, and the
  // percentages offered as one-tap buttons. The server refuses a reasonless
  // discount on its own when required; checking here only saves the round trip.
  const reasonRequired = String(settings?.discount_reason_required ?? "false").toLowerCase() === "true";
  const presets = String(settings?.discount_presets || "")
    .split(",")
    .map(v => Number(v.trim()))
    .filter(v => Number.isFinite(v) && v > 0 && (cap === null || v <= cap));

  const apply = () => {
    setError(null);
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0) {
      return setError("A discount cannot be negative.");
    }
    if (cap !== null && rule === "pct" && amount > cap) {
      return setError(`The configured cap is ${cap}%.`);
    }
    if (cap !== null && rule === "flat" && subtotal && amount > (subtotal * cap) / 100) {
      return setError(`That is more than the ${cap}% cap allows on this bill.`);
    }
    if (amount > 0 && reasonRequired && !reason.trim()) {
      return setError("This outlet needs a reason for every discount. It prints on the bill and is audited.");
    }
    onApply(
      rule === "pct"
        ? { discount: 0, discountPct: amount }
        : { discount: amount, discountPct: null },
      reason.trim() || null
    );
    toast.success(
      amount > 0
        ? `Discount set — it applies when order #${order.orderNumber} is billed`
        : "Discount cleared"
    );
    onDone();
  };

  const clear = () => {
    onClear();
    setValue(0);
    setReason("");
    toast.success("Discount cleared");
    onDone();
  };

  return (
    <div className="space-y-base">
      {presets.length > 0 && (
        <div className="flex flex-wrap gap-snug" data-testid="discount-presets">
          {presets.map(p => (
            <Button
              key={p}
              type="button"
              variant={rule === "pct" && Number(value) === p ? "default" : "outline"}
              className="h-11 min-w-16"
              onClick={() => { setRule("pct"); setValue(p); }}
              data-testid={`discount-preset-${p}`}
            >
              {p}%
            </Button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-snug">
        <label className="flex flex-col gap-tight text-meta font-medium">
          Rule
          <select
            className="h-11 rounded-md border bg-background px-snug text-body"
            value={rule}
            onChange={e => setRule(e.target.value)}
            data-testid="discount-rule-select"
          >
            <option value="pct">Percentage</option>
            <option value="flat">Flat amount</option>
          </select>
        </label>
        <label className="flex flex-col gap-tight text-meta font-medium">
          {rule === "pct" ? "Percent" : "Amount (₹)"}
          <Input
            className="h-11"
            type="number"
            min={0}
            value={value}
            onChange={e => setValue(Number(e.target.value))}
            data-testid="discount-value-input"
          />
        </label>
      </div>
      <label className="flex flex-col gap-tight text-meta font-medium">
        {reasonRequired ? "Reason" : "Reason (optional)"}
        <Input
          className="h-11"
          placeholder="Why is this discounted?"
          value={reason}
          onChange={e => setReason(e.target.value)}
          data-testid="discount-reason-input"
        />
      </label>

      <p className="text-meta text-muted-foreground" data-testid="discount-summary">
        {cap !== null && `Cap ${cap}% · `}subtotal ₹{preview?.subtotal ?? "0"} · discount
        now ₹{preview?.discount_amount ?? "0"} · total ₹{preview?.total ?? "0"}
      </p>
      {tierPct > 0 && applied > 0 && (
        <p className="text-meta text-muted-foreground" data-testid="discount-tier-note">
          {/* Worth saying out loud: a cashier seeing a discount they did not type
              should know why, not assume they fat-fingered something. */}
          {tierPct}% of this is the guest's membership tier. Naming a discount here
          replaces it rather than stacking on it.
        </p>
      )}
      <p className="text-meta text-muted-foreground">
        Applied when the bill is printed or settled. The totals above come from
        the server, not from this screen.
      </p>

      {error && (
        <p className="text-meta font-semibold text-destructive" data-testid="discount-error">
          {error}
        </p>
      )}
      <div className="grid grid-cols-2 gap-snug">
        <Button variant="outline" className="h-11" onClick={clear} data-testid="billing-discount-clear-button">
          Clear
        </Button>
        <Button variant="action" className="h-11" onClick={apply} data-testid="billing-discount-save-button">
          Set discount
        </Button>
      </div>
    </div>
  );
}
